// fluids.ts
// Utilities to generate responsive (fluid) values for MUI `sx` based on theme breakpoints.
// The idea: for each target breakpoint, compute a ratio = bpWidth / referenceWidth,
// then scale base CSS values (only whitelisted units, e.g. px) by that ratio.

import type { Theme } from "@mui/material";

type BreakpointKey = keyof Theme["breakpoints"]["values"];
type ScaleMap = Partial<Record<BreakpointKey, Partial<Record<string, number>>>>;
type Options = {
  precision?: number;
  referenceBp?: BreakpointKey;
  unitsToScale?: Iterable<string>;
  unitlessProps?: Iterable<string>;
  skipZeroRatio?: boolean;
  scale?: ScaleMap;
};

/** Which units we actually scale. By default we only scale `px`. */
const DEFAULT_UNITS_TO_SCALE = new Set(["px"]);

/** CSS properties that are *unitless*, where numeric values should NOT be auto-turned into `px`.
 * Also, by default we do not scale these numeric values either (e.g. `fontWeight`).
 */
const DEFAULT_UNITLESS_PROPS = new Set([
  "lineHeight",
  "fontWeight",
  "opacity",
  "zIndex",
  "flexGrow",
  "flexShrink"
]);

/** Default target breakpoints. Feel free to override per call. */
export const DEFAULT_TARGETS = ["xs", "sm", "md", "lg", "xl", "xxl", "xxxl"] as const;

const fmt = (n: number, precision = 2) =>
  parseFloat(n.toFixed(precision)).toString();

/** Normalize a single token like `47x` -> `47px`, or keep keywords like `auto`. */
const normalizeToken = (raw: string): string => {
  const t = raw.trim();

  // Pass-through well-known keywords
  if (/^(auto|inherit|initial|unset|revert)$/i.test(t)) return t;

  // number + (optional) unit
  const m = t.match(/^(-?\d+(?:\.\d+)?)([a-z%]*)$/i);
  if (!m) return t;

  const num = m[1];
  let unit = (m[2] || "px").toLowerCase();

  // Allowed CSS length units (a compact whitelist)
  const allowed = new Set([
    "px", "%", "em", "rem", "vh", "vw", "vmin", "vmax", "ch", "ex",
    "cm", "mm", "in", "pt", "pc", "q"
  ]);

  // Auto-fix common typos; fallback to px
  if (!allowed.has(unit)) unit = "px";

  return `${num}${unit}`;
};

/** Scale a single token only if its unit is allowed to scale (e.g. px). */
const scaleOne = (
  token: string,
  ratio: number,
  precision: number,
  unitsToScale: Set<string>
): string => {
  const norm = normalizeToken(token);
  const m = norm.match(/^(-?\d+(?:\.\d+)?)([a-z%]+)$/i);
  if (!m) return norm;

  const num = parseFloat(m[1]);
  const unit = m[2].toLowerCase();

  if (!unitsToScale.has(unit)) return norm; // do not scale %, em, rem, etc.

  return `${fmt(num * ratio, precision)}${unit}`;
};

/** Split a CSS shorthand string by spaces but keep parentheses blocks intact (e.g. calc(...)). */
const splitCssShorthand = (v: string) => {
  const out: string[] = [];
  let buf = "";
  let depth = 0;
  for (const ch of v.trim()) {
    if (ch === "(") depth++;
    if (ch === ")") depth = Math.max(0, depth - 1);
    if (/\s/.test(ch) && depth === 0) {
      if (buf) {
        out.push(buf);
        buf = "";
      }
    } else {
      buf += ch;
    }
  }
  if (buf) out.push(buf);
  return out;
};

/**
 * Scale a full CSS value safely:
 * - If it's a number, treat it as `px` for *most* props (unless the prop is unitless).
 * - If it's `calc(...)`, scale only embedded numbers with whitelisted units.
 * - Otherwise split as a shorthand and scale each token separately (respecting unit whitelist).
 */
export const scaleCssValue = (
  prop: string,
  value: string | number,
  ratio: number,
  precision: number,
  unitsToScale: Set<string>,
  unitlessProps: Set<string>
): string => {
  // Numeric value: keep unitless props unscaled & unitless; otherwise treat as px and scale.
  if (typeof value === "number") {
    if (unitlessProps.has(prop)) {
      // Keep as-is (and unitless)
      return fmt(value, precision);
    }
    return `${fmt(value * ratio, precision)}px`;
  }

  const s = String(value).trim();

  // calc(...) → scale whitelisted unit numbers inside
  if (/^calc\(/i.test(s)) {
    return s.replace(
      /(-?\d+(?:\.\d+)?)([a-z%]+)/gi,
      (match, num, unit) =>
        unitsToScale.has(String(unit).toLowerCase())
          ? `${fmt(parseFloat(num) * ratio, precision)}${unit}`
          : match
    );
  }

  // Regular shorthands: split and scale each token
  const parts = splitCssShorthand(s);
  return parts.map((p) => scaleOne(p, ratio, precision, unitsToScale)).join(" ");
};

/** Compute ratio for a given breakpoint: bpWidth / referenceWidth (max by default). */
const makeRatio = (
  theme: Theme,
  bp: BreakpointKey,
  referenceBp?: BreakpointKey
) => {
  const map = theme.breakpoints.values;
  const base = referenceBp ? map[referenceBp] : Math.max(...Object.values(map));

  let cur = map[bp];

  // fix for zero-values
  if (cur === 0) {
    const nonZero = Object.values(map)
      .filter((v) => v > 0)
      .sort((a, b) => a - b)[0];

    if (nonZero != null) {
      cur = nonZero;
    }
  }

  if (!base || cur == null) return 0;
  return cur / base;
};

// helper: read multiplier for given bp + prop; default 1
const getScaleFactor = (bp: BreakpointKey, prop: string, scale?: ScaleMap) => {
  const val = scale?.[bp]?.[prop];
  return typeof val === "number" && Number.isFinite(val) ? val : 1;
};

/**
 * Build an `sx`-ready object with media queries for a single CSS property.
 * For each target breakpoint, the property's value is scaled by:
 *   scaled = base * (bp/reference) * (scale[bp][prop] ?? 1)
 *
 * @param theme       MUI theme
 * @param cssProp     CSS property name (e.g. 'padding', 'fontSize')
 * @param baseValue   Base value (string | number | string[] shorthand)
 * @param targets     Breakpoints to generate (e.g. ['md','lg'])
 * @param options
 *   - precision       Rounding precision (default 2)
 *   - referenceBp     Use a specific bp as reference instead of max width
 *   - unitsToScale    Which units to scale (default: ['px'])
 *   - unitlessProps   Props considered unitless; numeric values stay unitless
 *   - skipZeroRatio   Skip bps where ratio === 0 (e.g. xs: 0)
 *   - scale           Per-bp per-prop extra multipliers (e.g. { md:{ fontSize:2 } })
 */
export function getFluids(
  theme: Theme,
  cssProp: string,
  baseValue: string | number | Array<string | number>,
  targets: BreakpointKey[],
  options?: Options
): Record<string, Record<string, string>> {
  const precision = options?.precision ?? 2;
  const unitsToScale = new Set(options?.unitsToScale ?? DEFAULT_UNITS_TO_SCALE);
  const unitlessProps = new Set(options?.unitlessProps ?? DEFAULT_UNITLESS_PROPS);

  const out: Record<string, Record<string, string>> = {};

  // Normalize base to a single string (arrays become typical CSS shorthand)
  const base = Array.isArray(baseValue) ? baseValue.join(" ") : (baseValue as any);

  targets.forEach((bp) => {
    const baseRatio = makeRatio(theme, bp, options?.referenceBp);
    if (options?.skipZeroRatio && baseRatio === 0) return;

    // Multiply by optional per-bp multiplier for this property
    const ratio = baseRatio * getScaleFactor(bp, cssProp, options?.scale);

    const scaled = scaleCssValue(
      cssProp,
      base,
      ratio,
      precision,
      unitsToScale,
      unitlessProps
    );

    out[theme.breakpoints.up(bp)] = { [cssProp]: scaled } as Record<
      string,
      string
    >;
  });

  return out;
}

/** Convenience wrapper to use directly in `sx` as a function of theme. */
export const getFluidsSx =
  (
    cssProp: string,
    baseValue: string | number | Array<string | number>,
    targets: BreakpointKey[] = [...DEFAULT_TARGETS],
    options?: Parameters<typeof getFluids>[4]
  ) =>
    (theme: Theme) =>
      getFluids(theme, cssProp, baseValue, targets, options);

/**
 * Walk a style object and add media-query rules for ONLY the specified keys.
 * - Base values are NOT changed.
 * - For each target breakpoint, we compute scaled values and insert them under the bp key,
 *   UNLESS an explicit override for that property already exists in that breakpoint block.
 * @example
 *   getFluidsByKeys(theme, { fontSize:'20px', padding:'20px 40px' }, ['fontSize','padding'], ['sm','md'])
 */
export function getFluidsByKeys(
  theme: Theme,
  base: Record<string, any>,
  keysToScale: string[] = Object.keys(base),
  targets: BreakpointKey[] = [...DEFAULT_TARGETS],
  options?: Options
): Record<string, any> {
  const precision = options?.precision ?? 2;
  const unitsToScale = new Set(options?.unitsToScale ?? DEFAULT_UNITS_TO_SCALE);
  const unitlessProps = new Set(options?.unitlessProps ?? DEFAULT_UNITLESS_PROPS);

  const result: Record<string, any> = { ...base };

  targets.forEach((bp) => {
    const baseRatio = makeRatio(theme, bp, options?.referenceBp);
    if (options?.skipZeroRatio && baseRatio === 0) return;

    const mq = theme.breakpoints.up(bp);
    const existingBlock = (result[mq] ?? {}) as Record<string, any>;
    const nextBlock: Record<string, any> = { ...existingBlock };

    keysToScale.forEach((prop) => {
      const baseVal = base[prop];
      if (baseVal == null) return;

      if (Object.prototype.hasOwnProperty.call(existingBlock, prop)) return;

      const effRatio = baseRatio * getScaleFactor(bp, prop, options?.scale);

      nextBlock[prop] = scaleCssValue(
        prop,
        baseVal,
        effRatio,
        precision,
        unitsToScale,
        unitlessProps
      );
    });

    if (Object.keys(nextBlock).length) {
      result[mq] = nextBlock;
    }
  });

  return result;
}


export const getFluidsByKeysFunc = (
  base: Record<string, any>,
  keysToScale: string[] = Object.keys(base),
  targets: BreakpointKey[] = [...DEFAULT_TARGETS],
  options?: Options
): any => {
  const mergedOptions: Options = {
    skipZeroRatio: true,
    ...options,
  };

  return (theme: Theme): ReturnType<typeof getFluidsByKeys> => {
    return getFluidsByKeys(theme, base, keysToScale, targets, mergedOptions);
  };
};

/** Infer keys to scale: only primitive CSS values (string | number) from `base` */
const inferKeysToScale = (base: Record<string, any>): string[] =>
  Object.keys(base).filter((k) => {
    const v = (base as any)[k];
    return typeof v === "string" || typeof v === "number";
  });

export const getFluidsSX =
  (
    cssProp: string,
    baseValue: string | number | Array<string | number>,
    targets: BreakpointKey[] = [...DEFAULT_TARGETS],
    options?: Options
  ) =>
    ({ theme }: { theme: Theme }) =>
      getFluids(theme, cssProp, baseValue, targets, options);

/**
 * Styled-friendly wrapper that can read custom props.
 * Use it directly inside `styled(Component)( getFluidsByKeysSXProps(...) )`
 */
export const getFluidsByKeysSXProps = <P extends {}>(
  base:
    | Record<string, any>
    | ((p: P & { theme: Theme }) => Record<string, any>),
  keysToScale?:
    | string[]
    | ((p: P & { theme: Theme }) => string[]),
  targets:
    | BreakpointKey[]
    | ((p: P & { theme: Theme }) => BreakpointKey[]) = [...DEFAULT_TARGETS],
  options?:
    | Options
    | ((p: P & { theme: Theme }) => Options | undefined)
) => {
  return (props: P & { theme: Theme }) => {
    const baseObj =
      typeof base === "function" ? base(props) : base;

    const keys =
      typeof keysToScale === "function"
        ? keysToScale(props)
        : (keysToScale ?? inferKeysToScale(baseObj));

    const tgs =
      typeof targets === "function"
        ? targets(props)
        : targets;

    const opts =
      typeof options === "function"
        ? options(props)
        : options;

    return getFluidsByKeys(props.theme, baseObj, keys, tgs, opts);
  };
};

export const scaleUntil = (props: any, options: any): any => {
  const { from, until} = props
  const index = DEFAULT_TARGETS.indexOf(until) + 1;
  let startIndex = 0;
  if (from) {
    const maybeStartIndex = DEFAULT_TARGETS.indexOf(from);
    if (maybeStartIndex >= 0) {
      startIndex = maybeStartIndex;
    }
  }
  if (index >= 0) {
    const res = DEFAULT_TARGETS.slice(
      startIndex, DEFAULT_TARGETS.indexOf(until) + 1
    ).reduce((acc, el) => {
      /* @ts-ignore */
      acc[el] = options;
      return acc;
    }, {});
    return res
  }
  return props;
}

export const convertToBreakpoints = (theme: Theme, obj: Record<string, any>, unit?: string, direction = 'up'): any => {
  return Object.entries(obj).reduce((acc, [k, v]) => {
    /* @ts-ignore */
    acc[theme.breakpoints[direction](k)] = unit ? `${v}${unit}` : v;
    return acc;
  },{})
}

export const convertToStyled = (theme: Theme, obj: Record<string, any>, unit?: string): any => {
  return Object.entries(obj).reduce((acc, [k, v]) => {
    /* @ts-ignore */
    acc[theme.breakpoints.up(k)] = unit ? `${v}${unit}` : v;
    return acc;
  },{})
}